import {InjectionToken} from '@angular/core';
import * as Sentry from '@sentry/capacitor';

import {reportHandledError, SentryCaptureContext} from './sentry-report';

/**
 * The slice of the Sentry API that application code uses at runtime.
 */
export interface SentryApi {
  setTag(key: string, value: string | number | boolean | null): void;
  setUser(user: {id?: string; email?: string} | null): void;
  /** See `reportHandledError` — this is the DI-injectable path to it. */
  captureException(error: unknown, context: SentryCaptureContext): void;
}

/**
 * Injectable indirection over the `@sentry/capacitor` module functions.
 *
 * It exists for testability: the Angular unit-test builder pre-bundles each spec,
 * so `vi.mock('@sentry/capacitor', …)` inside a spec never takes effect and the
 * real (uninitialized, silently no-op) SDK functions run instead — leaving nothing
 * to assert. Overriding this token in TestBed is the seam that makes Sentry
 * reporting observable.
 *
 * Deliberately `@sentry/capacitor` rather than `@sentry/angular`: calls made
 * through the Capacitor wrapper also reach the native SDK, so a native crash on a
 * kiosk carries the same user/tags as a JS error.
 */
export const SENTRY_API = new InjectionToken<SentryApi>('SENTRY_API', {
  providedIn: 'root',
  factory: () => ({
    setTag: (key, value) => Sentry.setTag(key, value),
    setUser: (user) => Sentry.setUser(user),
    captureException: reportHandledError,
  }),
});
